import React from 'react';
import { hydrate } from 'react-dom';
import { createInstance } from '@optimizely/react-sdk';
import createStore from 'store/create';
import initialize from 'utils/app/initialize';
import App from 'containers/App';
import { TrackingClient } from '@sparefoot/tracking-client/dist/TrackingClient';
import { ReactSegmentWrapper } from '@sparefoot/segment-react-wrapper';

const decodedState = window?.__PRELOADED_STATE__
	? JSON.parse(Buffer.from(window.__PRELOADED_STATE__, 'base64').toString())
	: null;

const store = createStore(decodedState, { rollbar: window.Rollbar });
initialize(store);

const { app, user } = store.getState();

// Get Optimizely context from server-side rendering
const datafileFromServer = window.__OPTIMIZELY_DATAFILE__;
const userFromServer = window.__OPTIMIZELY_USER__;

// Create Optimizely client from pre-fetched datafile
let optimizelyClient = null;
if (datafileFromServer) {
	try {
		optimizelyClient = createInstance({
			datafile: datafileFromServer,
		});
	} catch (err) {
		console.error('[Optimizely] Client initialization error:', err);
	}
}
if (optimizelyClient && userFromServer?.forcedDecisions?.length > 0) {
	// eslint-disable-next-line no-unused-vars
	for (const { flagKey, variationKey } of userFromServer.forcedDecisions) {
		try {
			optimizelyClient.setForcedDecision({ flagKey }, { variationKey });
		} catch (err) {
			console.error(
				`[Optimizely] Failed to set forced decision for ${flagKey}:`,
				err,
			);
		}
	}
}

TrackingClient.initialize(
	new ReactSegmentWrapper({
		writeKey: app.segmentWriteKey,
		visitId: user.visitId,
		environment: app.environment,
		siteId: app.id,
		host: app.baseUrl.replace(/^https?:\/\//i, ''),
		cdnURL: app.eventsCdnUrl,
		debug:
			process.env.NODE_ENV === 'development' &&
			process.env.RENDER_ENV === 'client',
		integrations: {
			'Segment.io': {
				apiHost: app.eventsApiUrl,
				protocol: 'https',
			},
		},
	}),
);

const render = (AppComponent) =>
	hydrate(
		<AppComponent
			store={store}
			optimizelyClient={optimizelyClient}
			optimizelyUser={userFromServer}
		/>,
		document.getElementById('js-page-content'),
	);

// Initialize Optimizely user and wait for ready before hydrating
(async () => {
	if (optimizelyClient) {
		try {
			const userId = userFromServer?.id || user.visitId;
			optimizelyClient.setUser({
				id: userId,
				attributes: userFromServer?.attributes,
			});

			await optimizelyClient.onReady({ timeout: 1000 });
		} catch (err) {
			console.warn(
				'[Optimizely] SDK ready timeout, hydrating anyway:',
				err,
			);
		}
	}

	render(App);

	// Clean up global variables after hydration
	delete window.__OPTIMIZELY_DATAFILE__;
	delete window.__OPTIMIZELY_USER__;
})();

if (module.hot) {
	module.hot.accept(['./containers/App', './pages/Bundles'], () => {
		const NextApp =
			require('./containers/App').default; /* eslint-disable-line global-require, max-len */
		render(NextApp);
	});
}
